import React, { useEffect, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import countryCodes from '../../../constants/countryCodes.json'
import { isNullOrWhiteSpace } from '../../../utilities/string'
import { PhoneNumber } from '../../Forms/PhoneNumber'
import { PhoneNumberInputProps } from '../types'
import { useDefaultValue } from '../useDefaultValue'
import { useValidationState, ValidationState } from '../useValidationState'
import { getValidationRules } from '../validation'

export type RHFPhoneNumberInputProps = {
  input: PhoneNumberInputProps
  readonly?: boolean
  id?: string
}

const getDefaultCountryCallingCode = () => {
  // Each item in the navigator.languages array typically contains at least a 2-letter ISO 639-1 code indicating
  // the users' language, which may or may not be followed by the ISO 3166-1 country code (and a dash). Extract
  // the country code and use it as a default value, if it exists.
  // Note that the array is ordered according to user preferences, so prioritize early entries.
  if (typeof window !== 'undefined' && navigator.languages) {
    for (const ietfLanguageTag of navigator.languages) {
      if (ietfLanguageTag?.length === 5) {
        const defaultCountryCode = ietfLanguageTag.substring(3)

        if (defaultCountryCode) {
          const defaultCountryCallingCode = countryCodes[defaultCountryCode]?.code

          if (defaultCountryCallingCode) {
            return `+${defaultCountryCallingCode}`
          }
        }
      }
    }
  }

  return ''
}

export const RHFPhoneNumberInput = ({ input, id }: RHFPhoneNumberInputProps) => {
  const { register } = useFormContext()

  const dropdownInputName = `${input.slug}__countryCode`
  const textInputName = `${input.slug}__text`

  const defaultCountryCallingCode = getDefaultCountryCallingCode()

  const dropdownInputValue = useWatch({
    defaultValue: defaultCountryCallingCode,
    name: dropdownInputName,
  })

  const textInputValue = useWatch({ name: textInputName })

  const inputValue = useMemo(
    () => `${dropdownInputValue ?? ''}${textInputValue ?? ''}`,
    [dropdownInputValue, textInputValue]
  )

  const validationRules = useMemo(() => getValidationRules(input), [input])

  // Register actual phone number field. Need to use useController since register does not support defaultValue
  // for some reason.
  const {
    field: { onChange },
  } = useController({
    defaultValue: inputValue,
    name: input.slug,
  })

  // Register dropdown field
  const { field: dropdownField } = useController({
    defaultValue: defaultCountryCallingCode,
    name: dropdownInputName,
    rules: validationRules,
  })

  // On mount, trigger form validation for dropdown input if it has a default value
  useDefaultValue({
    name: dropdownInputName,
    value: defaultCountryCallingCode,
  })

  // Keep the actual phone number field value in sync
  useEffect(() => {
    onChange(inputValue)
  }, [inputValue, onChange])

  const dropdownInputValidationState = useValidationState(dropdownInputName)
  const textInputValidationState = useValidationState(textInputName)

  let validationState: ValidationState

  // If any input has an error, display that error message. Otherwise we don't need to display anything.
  if (
    dropdownInputValidationState !== true &&
    !isNullOrWhiteSpace(dropdownInputValidationState)
  ) {
    validationState = dropdownInputValidationState
  } else if (
    textInputValidationState !== true &&
    !isNullOrWhiteSpace(textInputValidationState)
  ) {
    validationState = textInputValidationState
  } else {
    validationState = undefined
  }

  return (
    <PhoneNumber
      id={id}
      {...input}
      dropdownProps={{ ...dropdownField, validation: dropdownInputValidationState }}
      textInputProps={{
        // Register text input field
        ...register(textInputName, {
          ...validationRules,
        }),
        placeholder: input.placeholder,
        validation: textInputValidationState,
      }}
      required={!!validationRules?.required}
      validation={validationState}
    />
  )
}
